// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/eventCta.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/eventCta.js
//
// What the pinned footer should offer a guest, right now.
//
// ── Why this is a separate, pure module ────────────────────────────────────
//
// The footer is the one control most guests came to the invitation to use, and
// the wrong answer there is not cosmetic: offering "RSVP" on an event someone
// cannot see, or "Request to join" to somebody already on the guest list, is
// worse than offering nothing. There are enough inputs — privacy, approval,
// capacity, waitlist, payment, deadline, whether the event has already
// happened — that the combinations cannot be checked by reading the JSX.
//
// So the decision lives here, as a function of plain data, and is tested. The
// screen renders whatever comes back.
//
// ── Built on resolveRsvpGate, not beside it ────────────────────────────────
//
// rsvpGate.js already answers "may this person RSVP, and if not, why not".
// Re-deriving that here would create a second source of truth that drifts —
// and the original RSVP-not-showing bug came from exactly that kind of
// duplication. This translates a gate verdict into an action.

/** No action, but not an error either — the footer renders nothing. */
const NONE = { kind: 'none', label: null, disabled: true };

/**
 * @param {{
 *   gate: {kind: string, lockedBy?: string, reason?: string, canRequestAccess?: boolean,
 *          canJoinWaitlist?: boolean},
 *   rsvpStatus?: 'going'|'maybe'|'not_going'|null,
 *   isPast?: boolean,
 *   deadlinePassed?: boolean,
 *   onWaitlist?: boolean,
 *   hasDatePoll?: boolean,
 *   ticketing?: {
 *     mode: 'tickets'|'contribution'|null,
 *     hasTicket?: boolean,
 *     priceFrom?: number|null,
 *     payWhatYouWant?: boolean,
 *     salesOpen?: boolean|null,
 *     salesMessage?: string|null,
 *     shareDue?: number|null,
 *     sharePaid?: boolean,
 *   },
 * }} input
 * @returns {{kind: string, label: string|null, hint?: string, tone?: string, disabled?: boolean}}
 */
export function resolveEventCta({
  gate,
  rsvpStatus = null,
  isPast = false,
  deadlinePassed = false,
  onWaitlist = false,
  hasDatePoll = false,
  ticketing = null,
} = {}) {
  if (!gate || typeof gate.kind !== 'string') return NONE;

  // Still resolving. A spinner is honest; an action would be a guess.
  if (gate.kind === 'loading') return { kind: 'loading', label: null, disabled: true };

  // The host. Their footer is a different set of actions entirely.
  if (gate.kind === 'hidden') return NONE;

  // The access check failed. This is the state that used to render nothing at
  // all, which made a broken invitation look like one with no RSVP.
  if (gate.kind === 'error') {
    return { kind: 'retry', label: 'Try again', tone: 'danger', hint: "We couldn't check your invite." };
  }

  // ── Ticketed invitations ─────────────────────────────────────────────────
  //
  // Buying a ticket IS the reply, so a ticketed invitation never offers
  // Going / Maybe / Can't go. Offering them was actively misleading: a guest
  // who tapped Going held no ticket and would be turned away at the door.
  //
  // Access locks still come first — a private invitation needs admitting
  // before anyone can buy from it — so only the payment, capacity and
  // available verdicts reach the ticket actions.
  if (ticketing?.mode === 'tickets') {
    const accessLocked = gate.kind === 'locked'
      && !['payment', 'capacity'].includes(gate.lockedBy);

    if (accessLocked && gate.reason === 'signedOut') {
      return { kind: 'signIn', label: 'Sign in to buy tickets', tone: 'primary' };
    }

    if (!accessLocked && (gate.kind === 'available' || gate.kind === 'locked')) {
      return resolveTicketCta({ ticketing, isPast, soldOut: gate.lockedBy === 'capacity' });
    }
  }

  // A contribution is free to RSVP to; it becomes a payment only once the host
  // has entered the cost and chosen who shares it. Before that, and for
  // anyone not in the split, it is an ordinary RSVP and falls through.
  if (ticketing?.mode === 'contribution' && ticketing.shareDue != null) {
    return ticketing.sharePaid
      ? { kind: 'sharePaid', label: 'Share paid', disabled: true, hint: 'Thanks — the host has been paid.' }
      : { kind: 'payShare', label: `Pay your share · ₹${formatRupees(ticketing.shareDue)}`, tone: 'primary' };
  }

  if (gate.kind === 'locked') {
    switch (gate.lockedBy) {
      case 'payment':
        return { kind: 'buyPasses', label: 'Buy passes', tone: 'primary' };

      case 'capacity':
        if (onWaitlist) {
          return {
            kind: 'onWaitlist',
            label: "You're on the waitlist",
            disabled: true,
            hint: 'We’ll let you know if a spot opens.',
          };
        }
        return gate.canJoinWaitlist
          ? { kind: 'joinWaitlist', label: 'Join waitlist', tone: 'warning', hint: 'This event is full.' }
          : { kind: 'full', label: 'This event is full', disabled: true };

      case 'pending':
        return {
          kind: 'requestPending',
          label: 'Request sent',
          disabled: true,
          hint: 'The host is reviewing it.',
        };

      case 'rejected':
        return { kind: 'requestRejected', label: 'Request declined', disabled: true };

      case 'guestList':
        return gate.canRequestAccess
          ? { kind: 'requestGuestList', label: 'Ask to be added', tone: 'primary',
              hint: 'This invitation is for a guest list.' }
          : { kind: 'guestListOnly', label: 'Guest list only', disabled: true };

      case 'approval':
      default:
        // Signed out is a lock too, but the way out is signing in, not asking
        // the host — telling someone to request access when the app simply
        // does not know who they are sends them down a dead end.
        if (gate.reason === 'signedOut') {
          return { kind: 'signIn', label: 'Sign in to RSVP', tone: 'primary' };
        }
        return gate.canRequestAccess
          ? { kind: 'requestAccess', label: 'Request to join', tone: 'primary' }
          : { kind: 'locked', label: 'Invite only', disabled: true };
    }
  }

  if (gate.kind !== 'available') return NONE;

  // ── Allowed to respond ───────────────────────────────────────────────────

  // A finished event keeps its page — photos and memories live there — but
  // asking someone whether they are coming is nonsense after the fact.
  if (isPast) return { kind: 'past', label: 'This event has ended', disabled: true };

  // A poll asks a different question, and it is answered inline where the
  // options are. Three fixed buttons cannot express it.
  if (hasDatePoll) return { kind: 'poll', label: 'Pick a date', tone: 'primary' };

  if (deadlinePassed) {
    return rsvpStatus
      ? { kind: 'closedWithRsvp', label: labelForStatus(rsvpStatus), disabled: true,
          hint: 'RSVPs have closed.' }
      : { kind: 'closed', label: 'RSVPs are closed', disabled: true };
  }

  return rsvpStatus
    ? { kind: 'rsvpChange', label: labelForStatus(rsvpStatus), tone: 'primary' }
    : { kind: 'rsvp', label: null };
}

/** How a saved answer reads back to the person who gave it. */
export function labelForStatus(status) {
  if (status === 'going') return "You're going";
  if (status === 'maybe') return 'You said maybe';
  if (status === 'not_going') return "You can't go";
  return null;
}

/** Does this CTA replace the three RSVP buttons, or sit alongside them? */
export function ctaReplacesRsvpButtons(cta) {
  return !!cta && cta.kind !== 'rsvp' && cta.kind !== 'rsvpChange';
}

/**
 * The ticket action for a guest who is allowed to buy.
 *
 * Kept separate so the order of these checks is readable on its own: a ticket
 * already held outranks everything, because "Your tickets" is what someone
 * standing at the door needs, including after sales close and after the event
 * has started.
 */
function resolveTicketCta({ ticketing, isPast, soldOut }) {
  if (ticketing.hasTicket) {
    return {
      kind: 'viewTickets',
      label: 'Your tickets',
      tone: 'primary',
      // Buying more is a real need — a friend decides late — but only while
      // there is something to buy.
      secondary: !isPast && ticketing.salesOpen && !soldOut
        ? { kind: 'buyTickets', label: 'Buy more' }
        : null,
    };
  }

  if (isPast) return { kind: 'past', label: 'This event has ended', disabled: true };

  // Unknown is not closed. Saying "not on sale" while the check is in flight
  // would tell a guest to give up on a ticket that is about to be available.
  if (ticketing.salesOpen == null) return { kind: 'loading', label: null, disabled: true };

  if (!ticketing.salesOpen) {
    return {
      kind: 'salesClosed',
      label: ticketing.salesMessage || 'Tickets are not on sale yet',
      disabled: true,
    };
  }

  if (soldOut) return { kind: 'full', label: 'Sold out', disabled: true };

  if (ticketing.payWhatYouWant) {
    return { kind: 'buyTickets', label: 'Pay what you want', tone: 'primary' };
  }

  return {
    kind: 'buyTickets',
    label: ticketing.priceFrom ? `Buy tickets · ₹${formatRupees(ticketing.priceFrom)}` : 'Buy tickets',
    tone: 'primary',
  };
}

/** ₹1,500 not ₹1500, and no trailing .00 on a whole-rupee price. */
function formatRupees(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return n.toLocaleString('en-IN', { maximumFractionDigits: Number.isInteger(n) ? 0 : 2 });
}
